/**
 * Bannière de perspective contextuelle (doctrine 2026-09-09).
 * Un instrument n'applique le contexte actif QUE via ce composant, qui ne
 * remonte que des ids réellement reliés (relations canoniques / liens explicites).
 * Aucun rattachement inféré : 0 objet relié = état vide honnête.
 */
import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";

export default function ContextScopeBanner({ module, onScope }) {
  const { i18n } = useTranslation();
  const en = (i18n.language || "fr").startsWith("en");
  const [data, setData] = useState(null);
  const [active, setActive] = useState(true);

  useEffect(() => {
    api.get("/workspace/agentic/context/linked").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const ids = data?.supported ? (module === "risques" ? data.risk_ids : data.document_ids) || [] : [];

  useEffect(() => {
    if (!data?.supported) { onScope?.(null); return; }
    onScope?.(active ? { ids: new Set(ids), label: data.context?.label } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, active]);

  if (!data?.supported) return null;
  const eng = data.context?.engagement ? (en ? data.context.engagement.en : data.context.engagement.fr)
    : (data.context?.object_type === "deal" ? "Deal" : (en ? "External organization" : "Organisation externe"));
  const n = ids.length;
  const noun = module === "risques" ? (en ? "risk(s)" : "risque(s)") : "document(s)";

  return (
    <div className={`flex items-center gap-2 p-2.5 rounded-md border text-xs flex-wrap ${active ? "border-[#EF9F27]/40 bg-[#EF9F27]/10 text-[#EF9F27]" : "border-white/10 bg-white/[0.03] text-zinc-400"}`}
      data-testid={`context-scope-banner-${module}`}>
      <Sparkles className="w-3.5 h-3.5 shrink-0" />
      <span>
        {en ? "Perspective" : "Perspective"} : <span className="font-medium">{eng} · {data.context?.label}</span>
        {" — "}
        {active
          ? (n > 0
            ? (en ? `filtered on ${n} linked ${noun}` : `filtré sur ${n} ${noun} relié(s)`)
            : (en ? `no ${noun} linked to this context (canonical links only)` : `aucun ${noun} relié à ce contexte (liens canoniques uniquement)`))
          : (en ? `${n} linked ${noun} available` : `${n} ${noun} relié(s) disponibles`)}
      </span>
      <button onClick={() => setActive((v) => !v)} data-testid={`context-scope-toggle-${module}`}
        className="ml-auto inline-flex items-center gap-1 underline-offset-2 hover:underline">
        {active ? (<><X className="w-3 h-3" /> {en ? "See all" : "Tout voir"}</>) : (en ? "Filter on this context" : "Filtrer sur ce contexte")}
      </button>
    </div>
  );
}
